/**
 * ProjectImageCarousel
 *
 * An Airbnb-inspired image carousel for project cards.
 * Features: auto-advance, smooth crossfade transitions, dot indicators,
 * hover pause, left/right arrows, and a beautiful fallback gradient.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";

interface ProjectImageCarouselProps {
  /** Array of image URLs */
  images: string[];
  /** Aspect ratio as CSS value, e.g. "16/10" */
  aspectRatio?: string;
  /** Auto-advance interval in ms (0 = disabled) */
  autoAdvanceMs?: number;
  /** Show dot indicators */
  showDots?: boolean;
  /** Show navigation arrows on hover */
  showArrows?: boolean;
  /** Show image count badge */
  showCount?: boolean;
  /** Fallback project name for gradient placeholder */
  projectName?: string;
  /** Fallback status color for gradient accent */
  statusColor?: string;
  /** Border radius for the carousel container */
  borderRadius?: string;
  /** Height override (use instead of aspect ratio) */
  height?: string | number;
  /** Called when clicking the carousel (not arrows) */
  onClick?: () => void;
}




export function ProjectImageCarousel({
  images,
  aspectRatio = "16/10",
  autoAdvanceMs = 5000,
  showDots = true,
  showArrows = true,
  showCount = true,
  projectName,
  statusColor,
  borderRadius = "12px",
  height,
  onClick,
}: ProjectImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasMultiple = images.length > 1;

  // Preload next image
  useEffect(() => {
    if (images.length <= 1) return;
    const nextIdx = (currentIndex + 1) % images.length;
    if (!loadedImages.has(nextIdx)) {
      const img = new Image();
      img.src = images[nextIdx];
      img.onload = () => {
        setLoadedImages((prev) => new Set(prev).add(nextIdx));
      };
    }
  }, [currentIndex, images, loadedImages]);

  // Auto-advance timer
  useEffect(() => {
    if (!hasMultiple || autoAdvanceMs <= 0 || isHovered) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      goToNext();
    }, autoAdvanceMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMultiple, autoAdvanceMs, isHovered, currentIndex, images.length]);

  const goToNext = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [images.length]);

  const goToPrev = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [images.length]);

  const goToIndex = useCallback((index: number) => {
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, []);

  // No images, render default construction image
  if (images.length === 0) {
    return (
      <div
        onClick={onClick}
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: height ? undefined : aspectRatio,
          height: height || undefined,
          borderRadius,
          overflow: "hidden",
          cursor: onClick ? "pointer" : "default",
        }}
      >
        <img
          src="/default-project.png"
          alt={projectName || "Construction project"}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            filter: "brightness(0.85) saturate(0.9)",
          }}
        />
        {/* Subtle dark overlay for consistency */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, oklch(0.12 0.01 250 / 0.45), oklch(0.2 0.01 250 / 0.15))",
            pointerEvents: "none",
          }}
        />
        {/* Status accent bar */}
        {statusColor && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              background: statusColor,
              opacity: 0.7,
              zIndex: 2,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: height ? undefined : aspectRatio,
        height: height || undefined,
        borderRadius,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        background: "oklch(0.15 0.01 250)",
      }}
    >
      {/* Image layers: crossfade */}
      {images.map((src, idx) => (
        <div
          key={src}
          style={{
            position: "absolute",
            inset: 0,
            opacity: idx === currentIndex ? 1 : 0,
            transition: "opacity 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
            zIndex: idx === currentIndex ? 1 : 0,
            willChange: isTransitioning ? "opacity" : "auto",
          }}
        >
          <img
            src={src}
            alt={`${projectName || "Project"} photo ${idx + 1}`}
            loading={idx === 0 ? "eager" : "lazy"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
      ))}

      {/* Bottom gradient for readability */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "40%",
          background:
            "linear-gradient(to top, oklch(0.12 0.01 250 / 0.6), transparent)",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      {/* Navigation arrows */}
      {showArrows && hasMultiple && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              goToPrev();
            }}
            aria-label="Previous image"
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "none",
              background: "oklch(1 0 0 / 0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: isHovered ? 1 : 0,
              transition:
                "opacity 0.25s cubic-bezier(0.23, 1, 0.32, 1), transform 0.2s ease",
              boxShadow: "0 2px 8px oklch(0.1 0 0 / 0.3)",
            }}
            onMouseDown={(e) => {
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-50%) scale(0.92)";
            }}
            onMouseUp={(e) => {
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-50%) scale(1)";
            }}
          >
            <ChevronLeft size={16} color="oklch(0.2 0 0)" strokeWidth={2.5} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              goToNext();
            }}
            aria-label="Next image"
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "none",
              background: "oklch(1 0 0 / 0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: isHovered ? 1 : 0,
              transition:
                "opacity 0.25s cubic-bezier(0.23, 1, 0.32, 1), transform 0.2s ease",
              boxShadow: "0 2px 8px oklch(0.1 0 0 / 0.3)",
            }}
            onMouseDown={(e) => {
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-50%) scale(0.92)";
            }}
            onMouseUp={(e) => {
              (e.currentTarget as HTMLElement).style.transform =
                "translateY(-50%) scale(1)";
            }}
          >
            <ChevronRight size={16} color="oklch(0.2 0 0)" strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {showDots && hasMultiple && (
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            display: "flex",
            gap: 5,
          }}
        >
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                goToIndex(idx);
              }}
              aria-label={`Go to image ${idx + 1}`}
              style={{
                width: idx === currentIndex ? 18 : 6,
                height: 6,
                borderRadius: 3,
                border: "none",
                background:
                  idx === currentIndex
                    ? "oklch(1 0 0)"
                    : "oklch(1 0 0 / 0.5)",
                cursor: "pointer",
                padding: 0,
                transition:
                  "width 0.3s cubic-bezier(0.23, 1, 0.32, 1), background 0.3s ease",
              }}
            />
          ))}
        </div>
      )}

      {/* Image count badge */}
      {showCount && images.length > 1 && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 3,
            background: "oklch(0.15 0 0 / 0.7)",
            backdropFilter: "blur(8px)",
            borderRadius: 6,
            padding: "3px 8px",
            fontSize: "0.65rem",
            fontWeight: 600,
            color: "oklch(0.92 0 0)",
            letterSpacing: "0.03em",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ImageIcon size={10} />
          {currentIndex + 1}/{images.length}
        </div>
      )}
    </div>
  );
}
